import { User, UserActionSuccessTypeEnum } from '@sleipai/sleip-models';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import * as UserActions from '../actions/user.actions';

export const userFeatureKey = 'user';

export interface State extends EntityState<User> {
  totalUserCount: number;
  resultPerPage: number;
  numberOfPages: number;
  loading: boolean;
  loaded: boolean;
  userSaved: boolean;
  error: string | null;
  userActionSuccessType: UserActionSuccessTypeEnum | null;
  hasMoreUsers: boolean;
  lastSearchIds: string[] | null;
}

export function selectUserId(u: User) {
  return u.id ?? '';
}

export function sortByName(u1: User, u2: User) {
  return u1.name?.localeCompare(u2.name);
}

export const adapter: EntityAdapter<User> = createEntityAdapter<User>({
  selectId: selectUserId,
  sortComparer: sortByName,
});

export const initialState: State = adapter.getInitialState({
  totalUserCount: 0,
  resultPerPage: 0,
  numberOfPages: 0,
  loading: false,
  loaded: false,
  userSaved: false,
  error: null,
  userActionSuccessType: null,
  hasMoreUsers: false,
  lastSearchIds: null,
});

export const userReducer = createReducer(
  initialState,
  on(UserActions.loadUsers, (state) => {
    return {
      ...state,
      loading: true,
      loaded: false,
    };
  }),
  on(UserActions.loadUsersSuccess, (state, { users }) => {
    return adapter.upsertMany(users, {
      ...state,
      loaded: true,
      loading: false,
    });
  }),
  on(UserActions.loadSingleUser, (state) => {
    return {
      ...state,
      loading: true,
      loaded: false,
    };
  }),
  on(UserActions.addSingleUser, (state, { user }) => {
    return adapter.upsertOne(user, {
      ...state,
      loading: false,
      loaded: true,
    });
  }),
  on(UserActions.addUsers, (state, { users }) => {
    return adapter.addMany(users, {
      ...state,
      loading: false,
      loaded: true,
    });
  }),
  on(UserActions.loadUsersFailure, (state) => {
    return {
      ...state,
      loading: false,
      loaded: true,
    };
  }),
  on(
    UserActions.updateUserHorsesLoadStatus,
    (state, { horsesLoadStatusUpdatedUser }) => {
      return adapter.upsertOne(horsesLoadStatusUpdatedUser, {
        ...state,
        loading: false,
        loaded: true,
      });
    }
  ),
  on(UserActions.createUser, (state) => {
    return {
      ...state,
      loading: true,
      loaded: false,
    };
  }),
  on(UserActions.createUserSuccess, (state, { userActionSuccessType }) => {
    return {
      ...state,
      userSaved: true,
      loading: false,
      loaded: true,
      totalUserCount: state.totalUserCount + 1,
      userActionSuccessType: userActionSuccessType,
    };
  }),
  on(
    UserActions.loadUsersForInfiniteScrollList,
    (state, { reset = false }) => {
      const clearedState = reset ? adapter.removeAll(state) : state;
      return {
        ...clearedState,
        loaded: false,
        loading: true,
        lastSearchIds: null,
      };
    }
  ),
  on(UserActions.loadUsersFromAlgoliaSidb, (state) => {
    return {
      ...state,
      loading: true,
      loaded: false,
    };
  }),
  on(
    UserActions.loadUsersFromAlgoliaSidbSuccess,
    (state, { data, updateTotalCount = false }) => {
      return adapter.upsertMany(data.usersList, {
        ...state,
        totalUserCount: updateTotalCount
          ? data.totalUserCount
          : state.totalUserCount,
        resultPerPage: data.resultPerPage,
        numberOfPages: updateTotalCount
          ? data.numberOfPages
          : state.numberOfPages,
        loaded: true,
        loading: false,
        lastSearchIds: (data.page ?? 0) > 0
          ? [...(state.lastSearchIds ?? []), ...data.usersList.map((u: any) => u.id)]
          : data.usersList.map((u: any) => u.id),
      });
    }
  ),
  on(
    UserActions.loadUsersForInfiniteScrollListSuccess,
    (state, { users, hasMore }) => {
      return adapter.upsertMany(users, {
        ...state,
        hasMoreUsers: hasMore,
        loaded: true,
        loading: false,
      });
    }
  ),
  on(UserActions.createUserFailure, (state, { error }) => {
    return {
      ...state,
      loading: false,
      loaded: true,
      error: error?.message,
    };
  }),
  on(UserActions.actionUserFailure, (state, { error }) => {
    return {
      ...state,
      loading: false,
      loaded: true,
      error,
    };
  }),
  on(UserActions.resetUserSavedSuccess, (state) => {
    return {
      ...state,
      loading: false,
      loaded: true,
      userSaved: false,
      userActionSuccessType: null,
    };
  }),
  on(UserActions.updateUserRemoteShareRightsSetting, (state) => {
    return {
      ...state,
      loading: true,
      loaded: true,
      userActionSuccessType: null,
    };
  }),
  on(UserActions.updateUserDesktopWebAppAccessSetting, (state) => {
    return {
      ...state,
      loading: true,
      loaded: true,
      userActionSuccessType: null,
    };
  }),
  on(UserActions.updateUserIsAdminSetting, (state) => {
    return {
      ...state,
      loading: true,
      loaded: true,
      userActionSuccessType: null,
    };
  }),
  on(UserActions.updateUserBetaTesterSetting, (state) => {
    return {
      ...state,
      loading: true,
      loaded: true,
      userActionSuccessType: null,
    };
  }),
  on(UserActions.updateUserGroupSharingSetting, (state) => {
    return {
      ...state,
      loading: true,
      loaded: true,
      userActionSuccessType: null,
    };
  }),
  on(UserActions.updateUserCountry, (state) => {
    return {
      ...state,
      loading: true,
      loaded: true,
      userActionSuccessType: null,
    };
  }),
  on(UserActions.updateUserUserStatus, (state) => {
    return {
      ...state,
      loading: true,
      loaded: true,
      userActionSuccessType: null,
    };
  }),
  on(UserActions.updateUserTypeAppAccess, (state) => {
    return {
      ...state,
      loading: true,
      loaded: true,
      userActionSuccessType: null,
    };
  }),
  on(UserActions.updateUserEmail, (state) => {
    return {
      ...state,
      loading: true,
      loaded: true,
      userActionSuccessType: null,
    };
  }),
  on(UserActions.sendResetPasswordEmailForUser, (state) => {
    return {
      ...state,
      loading: true,
      loaded: true,
      userActionSuccessType: null,
    };
  }),
  on(
    UserActions.actionOnUserSuccess,
    (state, { update, userActionSuccessType }) => {
      if (update) {
        return adapter.updateOne(update, {
          ...state,
          loading: false,
          loaded: true,
          userActionSuccessType: userActionSuccessType,
        });
      }
      return {
        ...state,
        loading: false,
        loaded: true,
        userActionSuccessType: userActionSuccessType,
      };
    }
  ),
  on(UserActions.actionOnUserSuccessReset, (state) => {
    return {
      ...state,
      loading: false,
      loaded: true,
      userActionSuccessType: null,
    };
  }),
  on(
    UserActions.removeUserSuccess,
    (state, { userId, userActionSuccessType }) => {
      return adapter.removeOne(userId, {
        ...state,
        loading: false,
        totalUserCount: state.totalUserCount - 1,
        userActionSuccessType: userActionSuccessType,
      });
    }
  ),
  on(UserActions.loadAdminUsers, (state) => {
    return {
      ...state,
      loading: true,
      loaded: false,
    };
  }),
  on(UserActions.resetUserError, (state) => {
    return {
      ...state,
      loading: false,
      loaded: true,
      error: null,
    };
  })
);

export function reducer(state: State | undefined, action: Action) {
  return userReducer(state, action);
}

// get the selectors
const { selectIds, selectEntities, selectAll, selectTotal } =
  adapter.getSelectors();

// select the array of user ids
export const selectUserIds = selectIds;

// select the dictionary of user entities
export const selectUserEntities = selectEntities;

// select the array of users
export const selectAllUsersReducer = selectAll;

// select the total user count
export const selectUserTotal = selectTotal;
